import type { Role } from "@/types/api";

/**
 * Which subtree of the app belongs to each role, and so where each role lands
 * after signing in.
 *
 * A Record rather than a conditional: a fourth role then fails to compile
 * instead of silently landing somewhere it does not belong.
 *
 * In its own module rather than in guards.tsx so that a file of components
 * stays a file of components -- the route guards and the sign-in page both
 * read it.
 */
export const areaPath: Record<Role, string> = {
  admin: "/admin",
  client: "/app",
  verifier: "/verify",
};

/**
 * Where an account whose password is still provisional is held.
 *
 * In this module rather than in guards.tsx for the same reason `areaPath` is:
 * the guard that redirects there and the sign-in page that lands on it both
 * read it, and a file of components should stay a file of components.
 */
export const FIRST_PASSWORD_PATH = "/first-password";

/**
 * Whether a remembered path is somewhere this role may actually go.
 *
 * A path is remembered when RequireAuth turns an unauthenticated visitor away,
 * and it outlives the session that produced it: it lives in history state, so
 * it survives both a sign-out and a reload of the sign-in page. Handing it to
 * whoever signs in next is what sent a member to an administrator's page and
 * from there to /forbidden.
 */
export function canVisit(role: Role, target: string): boolean {
  // The remembered value carries the query string too, and only the path
  // decides which area it belongs to.
  const path = target.split("?")[0] ?? "/";
  // RoleLanding, which sends each role to its own area -- always safe.
  if (path === "/") return true;

  const area = areaPath[role];
  return path === area || path.startsWith(`${area}/`);
}
